<template>
  <div id="quality-check-panel">
    <div class="quality-check-buttons">
      <vf-button
        :busy="isChangingStatus"
        :disabled="isButtonDisabled(qualityCheckStatuses.rejected)"
        @click="setQualityCheckStatus(qualityCheckStatuses.rejected)"
        >{{ $t('qualityCheckPanel.reject') }}</vf-button
      >
      <vf-button
        :busy="isChangingStatus"
        :disabled="isButtonDisabled(qualityCheckStatuses.accepted)"
        :variant="buttonVariants.secondary"
        @click="setQualityCheckStatus(qualityCheckStatuses.accepted)"
        >{{ $t('qualityCheckPanel.accept') }}</vf-button
      >
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import VfButton from '@/components/VfButton/VfButton.vue';
  import endpointsList from '@/helpers/endpointsList';
  import axios from 'axios';
  import {
    IDigitalizationDocumentDetails,
    POSITIVE_DIGITALIZATION_FORMAL_CONTROL_STATUSES,
    QualityCheckStatus,
  } from 'types';
  import { ButtonVariant } from '../VfButton/VfButton.types';

  const props = defineProps<{
    digitalizationDocument: IDigitalizationDocumentDetails;
  }>();
  const emit = defineEmits<{ (e: 'document-data-changed'): void }>();

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();

  const buttonVariants = ref({
    primary: ButtonVariant.PRIMARY,
    secondary: ButtonVariant.SECONDARY,
  });

  const isChangingStatus = ref(false);

  const qualityCheckStatuses = ref({
    accepted: QualityCheckStatus.ACCEPTED,
    rejected: QualityCheckStatus.REJECTED,
  });

  function isButtonDisabled(buttonForStatus: QualityCheckStatus): boolean {
    return (
      !POSITIVE_DIGITALIZATION_FORMAL_CONTROL_STATUSES.includes(
        props.digitalizationDocument.formalControlStatus,
      ) ||
      props.digitalizationDocument.qualityCheckStatus ===
        QualityCheckStatus.ACCEPTED ||
      (buttonForStatus === QualityCheckStatus.REJECTED &&
        props.digitalizationDocument.qualityCheckStatus ===
          QualityCheckStatus.REJECTED)
    );
  }

  async function setQualityCheckStatus(
    qualityCheckStatus: QualityCheckStatus,
  ): Promise<void> {
    isChangingStatus.value = true;
    try {
      await axios.patch(
        endpointsList.digitalizationUploads.setQualityCheckStatusOfDocument(
          props.digitalizationDocument._id.toString(),
          qualityCheckStatus,
        ),
      );

      emit('document-data-changed');

      createSuccessToast(t('qualityCheckPanel.qualityCheckStatusSet'));
    } catch {
      createErrorToast(
        t('qualityCheckPanel.errors.errorSettingQualityCheckStatus.long'),
      );
    } finally {
      isChangingStatus.value = false;
    }
  }
</script>

<style lang="scss" scoped>
  #quality-check-panel {
    margin-top: 1.2rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 1rem;

    .quality-check-buttons {
      width: 100%;
      display: flex;
      row-gap: 1rem;
      justify-content: space-around;
    }
  }
</style>
